import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listAdminRatingsApi } from "api/admin";
import {
  C,
  Panel,
  SkeletonRows,
  Empty,
  Pagination,
  formatDate,
} from "./admin_ui";

function Stars({ score }) {
  const n = Math.max(0, Math.min(5, Number(score) || 0));
  return (
    <span style={{ color: "#F59E0B", fontSize: 13, letterSpacing: 1 }}>
      {"★".repeat(n)}
      <span style={{ color: C.line }}>{"★".repeat(5 - n)}</span>
    </span>
  );
}

export default function AdminRatings() {
  const [params, setParams] = useSearchParams();
  const expertId = params.get("expertId") || "";
  const page = parseInt(params.get("page") || "1", 10) || 1;
  const [data, setData] = useState({ items: [], total: 0, experts: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const r = await listAdminRatingsApi({
        expertId: expertId || undefined,
        page,
        limit: 20,
      });
      if (cancelled) return;
      if (r?.success)
        setData({
          items: r.items || [],
          total: r.total || 0,
          experts: r.experts || [],
        });
      else setError(r?.error || "조회 실패");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [expertId, page]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.set("page", "1");
    setParams(next, { replace: false });
  };

  return (
    <>
      <div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 800,
            color: C.ink,
            letterSpacing: "-0.02em",
          }}
        >
          평점 모니터링
        </div>
        <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
          {expertId
            ? `수리기사 ${expertId} 의 평점 — ${data.total.toLocaleString()}건`
            : `전체 ${data.total.toLocaleString()}건 — 수리기사를 클릭하면 해당 기사 평점만 봅니다.`}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.2fr)",
          gap: 14,
        }}
      >
        <Panel title="수리기사별 평점" count={data.experts?.length ?? 0}>
          {loading ? (
            <SkeletonRows count={6} />
          ) : data.experts?.length === 0 ? (
            <Empty />
          ) : (
            (data.experts || []).map((e) => {
              const isLow = e.avg < 3 && e.count >= 3;
              return (
                <div
                  key={e.expertId}
                  onClick={() => setParam("expertId", e.expertId)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 4px",
                    borderBottom: `1px solid ${C.line}`,
                    fontSize: 13,
                    cursor: "pointer",
                    background:
                      expertId === e.expertId ? "#FAFCFA" : "transparent",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      color: C.ink,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {e.expertName || e.expertId}
                  </div>
                  <Stars score={Math.round(e.avg)} />
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: isLow ? C.red700 : C.greenDark,
                      background: isLow ? C.red50 : C.greenBg,
                      padding: "3px 10px",
                      borderRadius: 9999,
                      minWidth: 56,
                      textAlign: "center",
                    }}
                  >
                    {e.avg.toFixed(2)} ({e.count})
                  </div>
                </div>
              );
            })
          )}
        </Panel>

        <Panel title={expertId ? "선택한 기사 평점" : "전체 평점 내역"} count={data.total}>
          {expertId && (
            <div style={{ marginBottom: 10 }}>
              <button
                type="button"
                onClick={() => setParam("expertId", "")}
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: `1px solid ${C.line}`,
                  background: "#fff",
                  color: C.body,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                ✕ 필터 해제
              </button>
            </div>
          )}
          {loading ? (
            <SkeletonRows count={8} />
          ) : error ? (
            <Empty label={error} />
          ) : data.items.length === 0 ? (
            <Empty />
          ) : (
            data.items.map((r) => (
              <div
                key={r.docId}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "10px 4px",
                  borderBottom: `1px solid ${C.line}`,
                  fontSize: 13,
                  alignItems: "center",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.ink, fontWeight: 600 }}>
                    {r.raterName || r.raterId}{" "}
                    <span style={{ color: C.sub, fontWeight: 400 }}>→</span>{" "}
                    {r.expertName || r.expertId}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11, color: C.sub }}>
                    {formatDate(r.createDate)}
                  </div>
                </div>
                <Stars score={r.score} />
              </div>
            ))
          )}
          <Pagination
            page={page}
            limit={20}
            total={data.total}
            onChange={(p) => setParam("page", String(p))}
          />
        </Panel>
      </div>
    </>
  );
}
